<script setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { usePage, Link } from "@inertiajs/vue3";
import SidenavWithSubmenu from "./SidenavWithSubmenu.vue";

import { useRoute } from "vue-router";
import api from "@/Plugins/axios";
import { useMenu } from "../../Composable/useMenu";

const route = useRoute();

const activeMenuCode = computed(() => {
    return route.path.split("/")[1] ?? "";
});

const { menus, activeMenuGroup } = useMenu();

const urlLandingPage = "landing-page";

const unapprovedCounts = ref({});
const isLoading = ref(false);

const badgeKeyForMenu = {
    "admin-hotel-user": "hoteliers",
    "administrator-public-approval": "public",
    "administrator-user-profile": "administrator",
};

const getBadgeCount = (menuCode) => {
    const key = badgeKeyForMenu[menuCode];
    return key ? unapprovedCounts.value[key] || 0 : 0;
};

const shouldBadge = (menuCode) => getBadgeCount(menuCode) > 0;

const getTotalChildrenBadges = (menu) => {
    if (!menu.children || menu.children.length === 0) return 0;

    let total = 0;
    menu.children.forEach((child) => {
        total += getBadgeCount(child.code);
        if (child.children && child.children.length > 0) {
            total += getTotalChildrenBadges(child);
        }
    });

    return total;
};

const shouldShowParentBadge = (menu) => {
    return getTotalChildrenBadges(menu) > 0;
};

const fetchBadgeCounts = async () => {
    if (isLoading.value) return;

    try {
        isLoading.value = true;
        const response = await api.get("/notifications/unapproved-count");

        if (response.data && response.data.unapprovedCounts) {
            unapprovedCounts.value = response.data.unapprovedCounts;
        }
    } catch (error) {
        console.error("Error fetching badge counts:", error);
    } finally {
        isLoading.value = false;
    }
};

let pollInterval = null;

onMounted(() => {
    fetchBadgeCounts();

    pollInterval = setInterval(() => {
        fetchBadgeCounts();
    }, 30000);

    window.addEventListener("notif-approved-received", fetchBadgeCounts);
    window.addEventListener("notif-approved-read", fetchBadgeCounts);
});

onUnmounted(() => {
    if (pollInterval) {
        clearInterval(pollInterval);
    }

    window.removeEventListener("notif-approved-received", fetchBadgeCounts);
    window.removeEventListener("notif-approved-read", fetchBadgeCounts);
});

const refreshBadgeCounts = () => {
    fetchBadgeCounts();
};

defineExpose({
    refreshBadgeCounts,
});
</script>

<template>
    <div id="layoutSidenav_nav">
        <nav
            class="sb-sidenav accordion sb-sidenav-dark bg-stb"
            id="sidenavAccordion"
        >
            <div class="sb-sidenav-menu">
                <div
                    v-if="activeMenuGroup"
                    class="menu-group-header mx-2 my-3 px-2 py-1 shadow text-white fw-bold"
                    :style="{
                        background: `url(${activeMenuGroup.image_header_sidemenu})`,
                    }"
                >
                    {{ activeMenuGroup.name }}
                </div>
                <div class="nav mt-3">
                    <RouterLink
                        v-if="activeMenuGroup?.code == 'survey'"
                        :to="{ name: 'admin-survey-data-select' }"
                        class="nav-link d-flex align-items-center mb-3"
                    >
                        <div class="sb-nav-link-icon">
                            <span class="material-icons">arrow_back</span>
                        </div>
                        <span>Back </span>
                    </RouterLink>
                    <RouterLink
                        v-if="activeMenuGroup?.code == 'external_data'"
                        :to="{ name: 'admin-external-data-select' }"
                        class="nav-link d-flex align-items-center mb-3"
                    >
                        <div class="sb-nav-link-icon">
                            <span class="material-icons">arrow_back</span>
                        </div>
                        <span>Back </span>
                    </RouterLink>
                    <template v-for="menu in menus" :key="menu.id">
                        <!-- MENU TYPE LINK -->
                        <RouterLink
                            v-if="menu.type === 0"
                            class="nav-link d-flex align-items-center"
                            :class="{ active: activeMenuCode == menu.code }"
                            :to="{ name: menu.code }"
                        >
                            <div class="sb-nav-link-icon">
                                <span class="material-icons">{{
                                    menu.icon
                                }}</span>
                            </div>

                            <span>{{ menu.name }}</span>

                            <span
                                v-if="shouldBadge(menu.code)"
                                class="badge bg-danger ms-auto"
                            >
                                {{ getBadgeCount(menu.code) }}
                            </span>
                        </RouterLink>

                        <!-- MENU TYPE HEADER -->
                        <div
                            v-if="menu.type == 1"
                            class="sb-sidenav-menu-heading"
                        >
                            {{ menu.name }}
                        </div>

                        <!-- MENU TYPE WITH CHILDREN -->
                        <SidenavWithSubmenu
                            v-if="menu.type == 2"
                            :menu="menu"
                            :unreadCounts="unapprovedCounts"
                            :badgeKeyForMenu="badgeKeyForMenu"
                            :getBadgeCount="getBadgeCount"
                            :shouldBadge="shouldBadge"
                            :getTotalChildrenBadges="getTotalChildrenBadges"
                            :shouldShowParentBadge="shouldShowParentBadge"
                            :isLoading="isLoading"
                        />
                    </template>
                    <RouterLink
                        v-if="activeMenuGroup"
                        class="nav-link mt-5"
                        :to="{ name: 'landing-page' }"
                    >
                        <div class="sb-nav-link-icon fw-bold">
                            <span class="material-icons"> house </span>
                            Back To Home
                        </div>
                    </RouterLink>
                </div>
            </div>
        </nav>
    </div>
</template>

<style scoped>
.task-notif {
    margin-left: auto;
}

.bg-stb {
    background: linear-gradient(92deg, rgb(0 0 0 / 50%), rgba(39, 39, 39, 0.8)),
        url(/assets/images/tds-side-background.jpeg);
    background-size: cover;
    /* Scale the image to cover the entire area */
    background-position: center;
    /* Center the image */
    background-repeat: no-repeat;
}

.menu-group-header {
    aspect-ratio: 16/7;
    background-size: cover !important;
    text-shadow: #000 0px 0px 3px;
    font-size: 1.2rem;
}
</style>
